import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import { Plus, Save, X, Trash2, Pin, PinOff, Send, ExternalLink, Image as ImageIcon, BarChart3, Eye, EyeOff } from "lucide-react";

const emptyPost = {
  title: "", body: "", image_url: "", link_url: "", link_text: "",
  kind: "post",
  status: "draft",
  publish_at: "", expires_at: "",
  pinned: false,
  poll: null,
};

const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <div className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60 mb-1">{label}</div>
    {children}
  </div>
);

const Input = (props) => (
  <input {...props} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
);
const Textarea = (props) => (
  <textarea {...props} rows={props.rows || 4} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
);
const Select = ({ options, ...rest }) => (
  <select {...rest} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const NetworkUpdateForm = ({ initial, onSubmit, onCancel, saving }) => {
  const [f, setF] = useState(initial || emptyPost);
  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setF(s => ({ ...s, [k]: v }));
  };
  const setPoll = (k) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setF(s => ({ ...s, poll: { ...(s.poll || { question: "", options: [{ text: "" }, { text: "" }], allow_multiple: false, closes_at: "", hide_results_until_close: false }), [k]: v } }));
  };
  const setOpt = (i, val) => {
    setF(s => {
      const p = s.poll || { question: "", options: [], allow_multiple: false };
      const opts = [...(p.options || [])];
      opts[i] = { ...(opts[i] || {}), text: val };
      return { ...s, poll: { ...p, options: opts } };
    });
  };
  const addOpt = () => setF(s => ({ ...s, poll: { ...(s.poll || {}), options: [...((s.poll && s.poll.options) || []), { text: "" }] } }));
  const removeOpt = (i) => setF(s => ({ ...s, poll: { ...(s.poll || {}), options: (s.poll?.options || []).filter((_, idx) => idx !== i) } }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      title: f.title,
      body: f.body,
      image_url: f.image_url || "",
      link_url: f.link_url || "",
      link_text: f.link_text || "",
      kind: f.kind,
      status: f.status,
      publish_at: f.publish_at || null,
      expires_at: f.expires_at || null,
      pinned: !!f.pinned,
    };
    if (f.kind === 'poll') {
      const p = f.poll || {};
      payload.poll = {
        question: p.question || "",
        options: (p.options || []).filter(o => (o.text || "").trim()).map(o => ({ id: o.id, text: o.text })),
        allow_multiple: !!p.allow_multiple,
        closes_at: p.closes_at || null,
        hide_results_until_close: !!p.hide_results_until_close,
      };
    }
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="rounded-lg p-6 bg-white border" style={{ borderColor: 'rgba(13,42,32,0.08)' }} data-testid="nu-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[18px] font-medium">{initial?.id ? "Edit Network Update" : "New Network Update"}</h3>
        <button type="button" onClick={onCancel} className="text-[13px] link-underline inline-flex items-center gap-1"><X size={14} /> Cancel</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Kind">
          <Select data-testid="nu-kind" value={f.kind} onChange={set('kind')} options={[{ value: 'post', label: 'Post' }, { value: 'poll', label: 'Poll' }]} />
        </Field>
        <Field label="Status">
          <Select data-testid="nu-status" value={f.status} onChange={set('status')} options={[{ value: 'draft', label: 'Draft' }, { value: 'scheduled', label: 'Scheduled' }, { value: 'published', label: 'Published' }]} />
        </Field>
        <Field label="Title" full><Input required data-testid="nu-title" value={f.title} onChange={set('title')} /></Field>
        <Field label="Body (HTML allowed)" full>
          <Textarea data-testid="nu-body" rows={6} value={f.body} onChange={set('body')} placeholder="<p>Your message…</p>" />
        </Field>
        <Field label="Image URL"><Input data-testid="nu-image" value={f.image_url} onChange={set('image_url')} placeholder="/api/uploads/…" /></Field>
        <Field label="CTA link URL"><Input data-testid="nu-link-url" value={f.link_url} onChange={set('link_url')} placeholder="https://…" /></Field>
        <Field label="CTA button text"><Input data-testid="nu-link-text" value={f.link_text} onChange={set('link_text')} placeholder="Read more" /></Field>
        <Field label="Pin to top">
          <label className="inline-flex items-center gap-2 text-[13px] mt-2"><input data-testid="nu-pinned" type="checkbox" checked={!!f.pinned} onChange={set('pinned')} /> Pinned</label>
        </Field>
        <Field label="Publish at (optional — for scheduled)"><Input type="datetime-local" data-testid="nu-publish-at" value={f.publish_at || ""} onChange={set('publish_at')} /></Field>
        <Field label="Expires at (optional)"><Input type="datetime-local" data-testid="nu-expires-at" value={f.expires_at || ""} onChange={set('expires_at')} /></Field>

        {f.kind === 'poll' && (
          <div className="md:col-span-2 rounded-md p-4" style={{ background: '#f4efe4' }}>
            <div className="text-[13px] font-medium mb-3 flex items-center gap-2"><BarChart3 size={14} /> Poll</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Question" full><Input data-testid="nu-poll-question" required value={f.poll?.question || ""} onChange={setPoll('question')} placeholder="What is the best time for the next meetup?" /></Field>
              <Field label="Closes at (optional)"><Input type="datetime-local" data-testid="nu-poll-closes-at" value={f.poll?.closes_at || ""} onChange={setPoll('closes_at')} /></Field>
              <Field label="Voting rules">
                <div className="flex flex-col gap-1 mt-1 text-[13px]">
                  <label className="inline-flex items-center gap-2"><input type="checkbox" data-testid="nu-poll-multiple" checked={!!f.poll?.allow_multiple} onChange={setPoll('allow_multiple')} /> Allow multiple selections</label>
                  <label className="inline-flex items-center gap-2"><input type="checkbox" data-testid="nu-poll-hide-results" checked={!!f.poll?.hide_results_until_close} onChange={setPoll('hide_results_until_close')} /> Hide results until poll closes</label>
                </div>
              </Field>
              <div className="md:col-span-2">
                <div className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60 mb-2">Options</div>
                <div className="space-y-2">
                  {(f.poll?.options || []).map((o, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input data-testid={`nu-poll-option-${i}`} placeholder={`Option ${i + 1}`} value={o.text || ""} onChange={e => setOpt(i, e.target.value)} />
                      {(f.poll?.options || []).length > 2 && (
                        <button type="button" onClick={() => removeOpt(i)} className="text-[#e0684d] p-2"><Trash2 size={14} /></button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addOpt} data-testid="nu-poll-add-option" className="mt-2 inline-flex items-center gap-1 text-[12px] link-underline"><Plus size={12} /> Add option</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center gap-2">
        <button type="submit" disabled={saving} data-testid="nu-save-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
        <button type="button" onClick={onCancel} className="text-[13px] link-underline">Cancel</button>
      </div>
    </form>
  );
};

const StatusPill = ({ status }) => {
  const styles = {
    draft: { bg: '#ece5d3', color: '#0d2a20' },
    scheduled: { bg: '#fde5c7', color: '#8a4a10' },
    published: { bg: '#d6ecd6', color: '#154a1c' },
    archived: { bg: '#f3d6d1', color: '#7a1f13' },
  };
  const s = styles[status] || styles.draft;
  return <span className="px-2 py-1 rounded text-[11px] font-medium uppercase tracking-wider" style={{ background: s.bg, color: s.color }}>{status}</span>;
};

const CMSNetworkUpdates = () => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [confirmSend, setConfirmSend] = useState(null); // { update, preview }
  const [pollResults, setPollResults] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/network-updates");
      setList(data);
    } catch { toast({ title: "Failed to load Network Updates" }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await adminApi.patch(`/admin/network-updates/${editing.id}`, payload);
        toast({ title: "Update saved" });
      } else {
        await adminApi.post("/admin/network-updates", payload);
        toast({ title: "Update created" });
      }
      setEditing(null); await load();
    } catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "Try again" }); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try {
      await adminApi.delete(`/admin/network-updates/${id}?confirm=true`);
      toast({ title: "Deleted" });
      setConfirmDelete(null); await load();
    } catch { toast({ title: "Delete failed" }); }
  };

  const togglePin = async (id) => {
    try { await adminApi.post(`/admin/network-updates/${id}/pin`); await load(); }
    catch { toast({ title: "Failed" }); }
  };
  const publish = async (id) => {
    try { await adminApi.post(`/admin/network-updates/${id}/publish`); toast({ title: "Published" }); await load(); }
    catch { toast({ title: "Failed" }); }
  };
  const unpublish = async (id) => {
    try { await adminApi.post(`/admin/network-updates/${id}/unpublish`); toast({ title: "Unpublished" }); await load(); }
    catch { toast({ title: "Failed" }); }
  };
  const openSendConfirm = async (u) => {
    setSendingEmailId(u.id);
    try {
      const { data } = await adminApi.get(`/admin/network-updates/${u.id}/email-preview?audience=active`);
      setConfirmSend({ update: u, preview: data, audience: "active" });
    } catch { toast({ title: "Failed to fetch recipients" }); }
    finally { setSendingEmailId(null); }
  };
  const changeAudience = async (audience) => {
    if (!confirmSend) return;
    try {
      const { data } = await adminApi.get(`/admin/network-updates/${confirmSend.update.id}/email-preview?audience=${audience}`);
      setConfirmSend({ ...confirmSend, preview: data, audience });
    } catch { toast({ title: "Failed to fetch recipients" }); }
  };
  const sendEmail = async () => {
    if (!confirmSend) return;
    const id = confirmSend.update.id;
    const audience = confirmSend.audience || "active";
    setSendingEmailId(id);
    try {
      const { data } = await adminApi.post(`/admin/network-updates/${id}/send-email?confirm=true&audience=${audience}`);
      toast({ title: "Email dispatched", description: `Queued to ${data.queued} recipient(s)` });
      setConfirmSend(null);
      await load();
    } catch (e) { toast({ title: "Email failed", description: e?.response?.data?.detail || "Try again" }); }
    finally { setSendingEmailId(null); }
  };
  const viewResults = async (id) => {
    try {
      const { data } = await adminApi.get(`/admin/network-updates/${id}`);
      setPollResults(prev => ({ ...prev, [id]: data.poll_results }));
    } catch { toast({ title: "Failed" }); }
  };

  if (editing) return <NetworkUpdateForm initial={editing} onSubmit={save} onCancel={() => setEditing(null)} saving={saving} />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="section-eyebrow text-[#0d2a20]/60 mb-1">Member Portal</div>
          <h2 className="hero-title text-[28px] leading-tight">Network Updates</h2>
        </div>
        <button onClick={() => setEditing({ ...emptyPost })} data-testid="nu-new-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px]"><Plus size={14} /> New Update</button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[14px] text-[#0d2a20]/60">Loading…</div>
      ) : list.length === 0 ? (
        <div className="p-10 text-center text-[14px] text-[#0d2a20]/60 rounded-lg" style={{ background: '#fbf8ef' }}>No Network Updates yet. Create the first one to greet members.</div>
      ) : (
        <div className="space-y-3">
          {list.map(u => (
            <div key={u.id} className="rounded-lg p-5 bg-white border" style={{ borderColor: 'rgba(13,42,32,0.08)' }} data-testid={`nu-row-${u.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <StatusPill status={u.status} />
                    {u.pinned && <span className="text-[11px] px-2 py-1 rounded font-medium" style={{ background: '#fde5c7', color: '#8a4a10' }}><Pin size={11} className="inline mr-1" />Pinned</span>}
                    {u.kind === 'poll' && <span className="text-[11px] px-2 py-1 rounded font-medium" style={{ background: '#e8dcc4', color: '#5a4a1a' }}>Poll</span>}
                    {u.email_sent_at && <span className="text-[11px] text-[#0d2a20]/60">Email sent {new Date(u.email_sent_at).toLocaleString()} · {u.email_sent_count}x</span>}
                  </div>
                  <div className="text-[16px] font-medium">{u.title}</div>
                  {u.image_url && <div className="text-[12px] text-[#0d2a20]/60 mt-1 truncate"><ImageIcon size={11} className="inline mr-1" /> {u.image_url}</div>}
                  {u.link_url && <div className="text-[12px] text-[#0d2a20]/60 mt-1 truncate"><ExternalLink size={11} className="inline mr-1" /> {u.link_text || u.link_url}</div>}
                </div>
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <button onClick={() => setEditing(u)} data-testid={`nu-edit-${u.id}`} className="px-3 py-1.5 rounded-md btn-ghost text-[12px]">Edit</button>
                  <button onClick={() => togglePin(u.id)} className="px-3 py-1.5 rounded-md btn-ghost text-[12px] inline-flex items-center gap-1">{u.pinned ? <><PinOff size={12} /> Unpin</> : <><Pin size={12} /> Pin</>}</button>
                  {u.status === 'published' ? (
                    <button onClick={() => unpublish(u.id)} className="px-3 py-1.5 rounded-md btn-ghost text-[12px] inline-flex items-center gap-1"><EyeOff size={12} /> Unpublish</button>
                  ) : (
                    <button onClick={() => publish(u.id)} data-testid={`nu-publish-${u.id}`} className="px-3 py-1.5 rounded-md btn-forest text-[12px] inline-flex items-center gap-1"><Eye size={12} /> Publish</button>
                  )}
                  {u.status === 'published' && (
                    <button onClick={() => openSendConfirm(u)} disabled={sendingEmailId === u.id} data-testid={`nu-send-email-${u.id}`} className="px-3 py-1.5 rounded-md btn-coral text-[12px] inline-flex items-center gap-1 disabled:opacity-60"><Send size={12} /> {sendingEmailId === u.id ? 'Loading…' : 'Send to members'}</button>
                  )}
                  {u.kind === 'poll' && (
                    <button onClick={() => viewResults(u.id)} className="px-3 py-1.5 rounded-md btn-ghost text-[12px] inline-flex items-center gap-1"><BarChart3 size={12} /> Results</button>
                  )}
                  <button onClick={() => setConfirmDelete(u)} data-testid={`nu-delete-${u.id}`} className="px-3 py-1.5 rounded-md text-[12px] inline-flex items-center gap-1" style={{ background: '#f3d6d1', color: '#7a1f13' }}><Trash2 size={12} /> Delete</button>
                </div>
              </div>
              {pollResults[u.id] && (
                <div className="mt-4 pt-3 border-t" style={{ borderColor: 'rgba(13,42,32,0.08)' }}>
                  <div className="text-[12px] text-[#0d2a20]/60 mb-2">{pollResults[u.id].total_voters} voter(s) · {pollResults[u.id].total_votes} vote(s)</div>
                  <div className="space-y-2">
                    {pollResults[u.id].options.map(opt => (
                      <div key={opt.id}>
                        <div className="flex items-center justify-between text-[13px] mb-1"><span>{opt.text}</span><span className="text-[#0d2a20]/60">{opt.count} · {opt.percent}%</span></div>
                        <div className="h-2 rounded-full" style={{ background: '#ece5d3' }}>
                          <div className="h-2 rounded-full" style={{ width: `${opt.percent}%`, background: '#e0684d' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(13,42,32,0.5)' }}>
          <div className="rounded-lg p-6 max-w-md w-full" style={{ background: '#fbf8ef' }} data-testid="nu-delete-confirm">
            <h3 className="text-[20px] font-medium mb-2">Permanently delete this update?</h3>
            <p className="text-[14px] text-[#0d2a20]/75 mb-4">This will permanently delete "<b>{confirmDelete.title}</b>" and any votes cast on it. This action cannot be undone.</p>
            <div className="flex items-center gap-2">
              <button onClick={() => del(confirmDelete.id)} data-testid="nu-delete-confirm-btn" className="px-4 py-2 rounded-md text-[13px]" style={{ background: '#7a1f13', color: 'white' }}>Yes, delete permanently</button>
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-md btn-ghost text-[13px]">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {confirmSend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(13,42,32,0.5)' }}>
          <div className="rounded-lg p-6 max-w-md w-full" style={{ background: '#fbf8ef' }} data-testid="nu-send-confirm">
            <div className="flex items-center gap-2 mb-3"><Send size={18} className="text-[#e0684d]" /><h3 className="text-[20px] font-medium">Send this update?</h3></div>

            <div className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60 mb-2">Audience</div>
            <div className="grid gap-2 mb-4">
              {[
                { value: 'active',       label: 'Active members only',                      hint: `Members with activated profiles · ${confirmSend.preview.active_count} recipient(s)` },
                { value: 'pending',      label: 'Approved · pending activation',             hint: `Approved applicants who haven't created a profile yet · ${confirmSend.preview.pending_count} recipient(s)` },
                { value: 'approved_all', label: 'All approved (active + pending)',           hint: `Everyone we've approved — the sum of both lists, de-duplicated by email` },
              ].map(opt => (
                <label key={opt.value} className="flex items-start gap-3 p-3 rounded-md cursor-pointer" style={{ background: confirmSend.audience === opt.value ? '#fde5c7' : 'transparent', border: '1px solid rgba(13,42,32,0.1)' }} data-testid={`nu-audience-${opt.value}`}>
                  <input type="radio" name="nu-audience" value={opt.value} checked={confirmSend.audience === opt.value} onChange={() => changeAudience(opt.value)} className="mt-1 accent-[#e0684d]" />
                  <div className="min-w-0">
                    <div className="text-[14px] font-medium">{opt.label}</div>
                    <div className="text-[12px] text-[#0d2a20]/70">{opt.hint}</div>
                  </div>
                </label>
              ))}
            </div>

            <p className="text-[14px] text-[#0d2a20]/75 mb-1">You are about to send <b>{confirmSend.update.title}</b> to</p>
            <div className="text-[28px] font-medium leading-none mb-1" data-testid="nu-send-recipient-count">{confirmSend.preview.recipient_count}</div>
            <p className="text-[13px] text-[#0d2a20]/70 mb-4">{confirmSend.preview.recipient_type} (real production recipients — not a test send).</p>
            {confirmSend.preview.already_sent_at && (
              <div className="mb-4 text-[12px] p-2 rounded-md" style={{ background: '#fde5c7', color: '#8a4a10' }}>
                This update was previously emailed on {new Date(confirmSend.preview.already_sent_at).toLocaleString()} · {confirmSend.preview.already_sent_count} time(s). Sending again will re-notify everyone.
              </div>
            )}
            <div className="flex items-center gap-2">
              <button onClick={sendEmail} disabled={sendingEmailId === confirmSend.update.id || !confirmSend.preview.recipient_count} data-testid="nu-send-confirm-btn" className="px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60">{sendingEmailId === confirmSend.update.id ? 'Sending…' : `Yes, send to ${confirmSend.preview.recipient_count} recipient(s)`}</button>
              <button onClick={() => setConfirmSend(null)} className="px-4 py-2 rounded-md btn-ghost text-[13px]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CMSNetworkUpdates;
